import { BLOCK_END, BLOCK_START, NAME_END, TOKEN_END, TOKEN_START } from './DocxPreviewMarkers';

/**
 * How a highlighted spot in a preview came about:
 * value/empty - merged by the server (empty = merged to nothing); block - a rich-text section;
 * unfilled - a {Token} still in merged output; known/unknown - a {Token} in a raw template that the merge will/won't fill.
 */
export type PreviewTokenKind = 'value' | 'empty' | 'block' | 'unfilled' | 'known' | 'unknown';

export interface IPreviewToken {
    token: string;
    kind: PreviewTokenKind;
    element: HTMLElement;
}

/** Prefix for every class docx-preview generates, so its rules can't collide with the app's own classes. */
export const DOCX_CLASS = 'lh-docx';

const TOKEN_ATTR = 'data-lh-token';
const KIND_ATTR = 'data-lh-kind';

const INLINE_VALUE = new RegExp(`${TOKEN_START}([^${NAME_END}]*)${NAME_END}([^${TOKEN_END}]*)${TOKEN_END}`, 'g');
const BLOCK_ANCHOR = new RegExp(`${BLOCK_START}([^${NAME_END}]*)${NAME_END}`);
const ANY_BLOCK_MARKER = new RegExp(`${BLOCK_START}[^${NAME_END}]*${NAME_END}|${BLOCK_END}`, 'g');
const RAW_TOKEN = /\{([A-Za-z0-9_]+)\}/g;
/** Word's own merge fields (Insert > Field > MergeField) display as «Name». LandHawk never fills them. */
const WORD_MERGE_FIELD = /«[A-Za-z0-9_]{1,40}»/g;

/**
 * Merged preview documents: turns the server's markers into spans, then flags whatever the merge left
 * behind - {Tokens} it doesn't know and Word merge fields.
 */
export function annotateMergedDocument(root: HTMLElement): IPreviewToken[] {
    markBlocks(root);
    paragraphsOf(root).forEach((p) => {
        markInlineValues(p);
        markLeftovers(p, RAW_TOKEN, () => 'unfilled');
        markLeftovers(p, WORD_MERGE_FIELD, () => 'unfilled');
    });
    return collectTokens(root);
}

/** Raw templates: marks every {Token}, split into ones the merge fills (isKnown) and ones it leaves as typed. */
export function annotateTemplate(root: HTMLElement, isKnown: (token: string) => boolean): IPreviewToken[] {
    paragraphsOf(root).forEach((p) => {
        markLeftovers(p, RAW_TOKEN, (token) => (isKnown(token) ? 'known' : 'unknown'));
        markLeftovers(p, WORD_MERGE_FIELD, () => 'unknown');
    });
    return collectTokens(root);
}

/** How a token reads in the document: {Name} for LandHawk tokens, «Name» for Word merge fields. */
export function displayToken(token: string): string {
    return token.charAt(0) === '«' ? token : `{${token}}`;
}

/** Current text of every highlighted value, by token, so a re-render can tell which ones changed. */
export function tokenValues(tokens: IPreviewToken[]): Map<string, string> {
    const values = new Map<string, string>();
    tokens.forEach((t) => values.set(t.token, (values.get(t.token) || '') + t.element.textContent));
    return values;
}

/** "LesseeAddressLine2" -> "Lessee Address Line 2" */
export function humanizeToken(token: string): string {
    return token
        .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .trim();
}

function paragraphsOf(root: HTMLElement): HTMLElement[] {
    return Array.prototype.slice.call(root.querySelectorAll('p'));
}

function collectTokens(root: HTMLElement): IPreviewToken[] {
    const elements: HTMLElement[] = Array.prototype.slice.call(root.querySelectorAll(`[${TOKEN_ATTR}]`));
    return elements.map((element) => ({
        token: element.getAttribute(TOKEN_ATTR),
        kind: element.getAttribute(KIND_ATTR) as PreviewTokenKind,
        element,
    }));
}

function tag(element: HTMLElement, token: string, kind: PreviewTokenKind): void {
    element.classList.add('lh-token', `lh-token--${kind}`);
    element.setAttribute(TOKEN_ATTR, token);
    element.setAttribute(KIND_ATTR, kind);
}

/** Rich-text blocks span paragraphs, so tag every paragraph from the anchor through the end marker. */
function markBlocks(root: HTMLElement): void {
    const paragraphs = paragraphsOf(root);
    let open: { token: string; from: number } = null;

    paragraphs.forEach((p, index) => {
        const text = p.textContent;
        const anchor = BLOCK_ANCHOR.exec(text);
        if (anchor) {
            open = { token: anchor[1], from: index };
        }
        if (open && text.indexOf(BLOCK_END) >= 0) {
            for (let i = open.from; i <= index; i++) {
                tag(paragraphs[i], open.token, 'block');
            }
            open = null;
        }
    });

    if (open) {
        tag(paragraphs[open.from], open.token, 'block');
    }

    textNodesOf(root).forEach((node) => {
        const stripped = node.nodeValue.replace(ANY_BLOCK_MARKER, '');
        if (stripped !== node.nodeValue) {
            node.nodeValue = stripped;
        }
    });
}

function markInlineValues(p: HTMLElement): void {
    const matches = matchAll(INLINE_VALUE, p.textContent);

    // Last to first, so earlier offsets stay valid as markers are removed.
    for (let i = matches.length - 1; i >= 0; i--) {
        const match = matches[i];
        const token = match[1];
        const value = match[2];
        const start = match.index;
        const valueStart = start + TOKEN_START.length + token.length + NAME_END.length;
        const valueEnd = valueStart + value.length;

        rangeOf(p, valueEnd, valueEnd + TOKEN_END.length).deleteContents();
        rangeOf(p, start, valueStart).deleteContents();

        const span = p.ownerDocument.createElement('span');
        if (value.trim().length === 0) {
            tag(span, token, 'empty');
            span.textContent = `«${humanizeToken(token)}»`;
            rangeOf(p, start, start + value.length).deleteContents();
            rangeOf(p, start, start).insertNode(span);
        } else {
            tag(span, token, 'value');
            const range = rangeOf(p, start, start + value.length);
            span.appendChild(range.extractContents());
            range.insertNode(span);
        }
    }
}

/** Wraps each match of pattern; the token is capture group 1, or the whole match when there is none. */
function markLeftovers(p: HTMLElement, pattern: RegExp, classify: (token: string) => PreviewTokenKind): void {
    const matches = matchAll(pattern, p.textContent);

    for (let i = matches.length - 1; i >= 0; i--) {
        const match = matches[i];
        const range = rangeOf(p, match.index, match.index + match[0].length);
        const container = range.startContainer.parentElement;
        if (container && container.closest(`[${TOKEN_ATTR}]`)) {
            continue;
        }

        const token = match[1] || match[0];
        const span = p.ownerDocument.createElement('span');
        tag(span, token, classify(token));
        span.appendChild(range.extractContents());
        range.insertNode(span);
    }
}

function matchAll(pattern: RegExp, text: string): RegExpExecArray[] {
    const matches: RegExpExecArray[] = [];
    const global = new RegExp(pattern.source, 'g');
    for (let match = global.exec(text); match; match = global.exec(text)) {
        matches.push(match);
    }
    return matches;
}

function textNodesOf(root: Node): Text[] {
    const nodes: Text[] = [];
    const walker = (root.ownerDocument || (root as Document)).createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        nodes.push(node as Text);
    }
    return nodes;
}

/**
 * A DOM range over characters [start, end) of an element's text. Word runs render as separate spans, and a
 * token routinely spans several of them, so offsets are resolved across all the element's text nodes.
 */
function rangeOf(element: HTMLElement, start: number, end: number): Range {
    const nodes = textNodesOf(element);
    const range = element.ownerDocument.createRange();
    if (nodes.length === 0) {
        range.selectNodeContents(element);
        range.collapse(true);
        return range;
    }

    let offset = 0;
    let startSet = false;
    for (const node of nodes) {
        const length = node.nodeValue.length;
        if (!startSet && start < offset + length) {
            range.setStart(node, start - offset);
            startSet = true;
        }
        if (startSet && end <= offset + length) {
            range.setEnd(node, end - offset);
            return range;
        }
        offset += length;
    }

    const last = nodes[nodes.length - 1];
    if (!startSet) {
        range.setStart(last, last.nodeValue.length);
    }
    range.setEnd(last, last.nodeValue.length);
    return range;
}



// WEBPACK FOOTER //
// ./src/components/common/docxPreview/docxPreviewDom.ts